/* Pythagorean Square (Квадрат Пифагора / Психоматрица) */

export interface PythagoreanResult {
  counts: Record<number, number>; // counts[1..9]
  wn1: number;
  wn2: number;
  wn3: number;
  wn4: number;
  lifePathNumber: number;  // wn1 reduced to single digit (keep 11,22,33)
  characterNumber: number; // wn3 reduced to single digit
  allDigits: number[];     // full digit pool used for grid
  // Derived "lines" (row/col/diagonal sums of counts)
  lineHealth: number;   // 1+4+7
  linePsycho: number;   // 2+5+8
  lineLogic: number;    // 3+6+9
  lineGoal: number;     // 1+2+3
  lineFamily: number;   // 4+5+6
  lineHabits: number;   // 7+8+9
  lineSelfEval: number; // 1+5+9
  lineLuck: number;     // 3+5+7
}

function digitsOf(n: number): number[] {
  return String(Math.abs(n))
    .split("")
    .map(Number);
}

function sumDigits(n: number): number {
  return digitsOf(n).reduce((a, b) => a + b, 0);
}

/** Reduce to single digit, preserving master numbers 11, 22, 33. */
function reduceToSingle(n: number): number {
  let val = Math.abs(n);
  while (val > 9 && val !== 11 && val !== 22 && val !== 33) {
    val = sumDigits(val);
  }
  return val;
}

export function calcPythagoreanSquare(birthdate: string): PythagoreanResult {
  // 1. Extract all digits from the date string (ignore dots)
  const dateDigits = birthdate.replace(/\D/g, "").split("").map(Number);

  // 2. WN1 = sum of all date digits
  const wn1 = dateDigits.reduce((a, b) => a + b, 0);

  // 3. WN2 = sum of digits of WN1
  const wn2 = sumDigits(wn1);

  // 4. WN3 = WN1 - 2 × (first digit of day part, or 0 if day starts with 0)
  // Day is the first two chars of the cleaned date string
  const firstDayDigit = dateDigits[0] === 0 ? 0 : dateDigits[0];
  const wn3 = wn1 - 2 * firstDayDigit;

  // 5. WN4 = sum of digits of |WN3|
  const wn4 = sumDigits(Math.abs(wn3));

  // All digits pool
  const pool = [
    ...dateDigits,
    ...digitsOf(wn1),
    ...digitsOf(wn2),
    ...digitsOf(Math.abs(wn3)),
    ...digitsOf(wn4),
  ];

  // Count occurrences of digits 1-9 (ignore 0)
  const counts: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0 };
  for (const d of pool) {
    if (d >= 1 && d <= 9) counts[d]++;
  }

  return {
    counts,
    wn1,
    wn2,
    wn3,
    wn4,
    lifePathNumber: reduceToSingle(wn1),
    characterNumber: reduceToSingle(wn3),
    allDigits: pool,
    lineHealth: counts[1] + counts[4] + counts[7],
    linePsycho: counts[2] + counts[5] + counts[8],
    lineLogic: counts[3] + counts[6] + counts[9],
    lineGoal: counts[1] + counts[2] + counts[3],
    lineFamily: counts[4] + counts[5] + counts[6],
    lineHabits: counts[7] + counts[8] + counts[9],
    lineSelfEval: counts[1] + counts[5] + counts[9],
    lineLuck: counts[3] + counts[5] + counts[7],
  };
}

export interface CellMeaning {
  digit: number;
  name: string;
  noDigits: string;
  oneDigit: string;
  twoDigits: string;
  threeDigits: string;
  manyDigits: string;
}

export const CELL_MEANINGS: Record<number, CellMeaning> = {
  1: {
    digit: 1,
    name: "Carácter",
    noDigits:
      "El desinterés y la entrega son tu naturaleza. Das con facilidad, pero tiendes a no notar tus propias necesidades. Es importante aprender a proteger tus límites personales.",
    oneDigit:
      "El carácter es suave, conciliador. Te preocupas por los demás más que por ti mismo. Sabes encontrar el compromiso, pero a veces te falta firmeza.",
    twoDigits:
      "El carácter es equilibrado: suficiente voluntad para alcanzar tus metas y suficiente flexibilidad para colaborar. Mantienes bien el balance entre tus intereses y los de los demás.",
    threeDigits:
      "Voluntad fuerte e individualidad marcada. Sabes lo que quieres y sabes mantenerte firme. Puede haber tendencia a la terquedad: es importante tener en cuenta la opinión de los demás.",
    manyDigits:
      "Carácter muy fuerte y a veces inflexible. Egocentrismo marcado y dificultad para ceder. Hace falta un trabajo consciente sobre la empatía y la capacidad de escuchar a los demás.",
  },
  2: {
    digit: 2,
    name: "Bioenergía",
    noDigits:
      "La energía vital es mínima. Es importante cuidar la salud con especial atención: horas de sueño, alimentación y cargas moderadas. Evita el agotamiento emocional.",
    oneDigit:
      "La bioenergía está por debajo de la media. Eres sensible a los cambios de ánimo y al ambiente que te rodea. Necesitas reponer tu recurso con regularidad: con descanso, naturaleza y silencio.",
    twoDigits:
      "Un nivel suficiente de energía vital. La salud es estable en general y tienes reservas de fuerza para una vida activa. Con un ritmo adecuado, la energía se gasta de forma equilibrada.",
    threeDigits:
      "Bioenergía alta. Un potencial vital poderoso, buena inmunidad y recuperación rápida. También llenas de energía a quienes te rodean: eres la «batería» del grupo.",
    manyDigits:
      "Fuerza vital excepcional. La energía literalmente te desborda: es importante canalizarla hacia algo productivo. Sin una salida adecuada, pueden aparecer sobreexcitación e impulsividad.",
  },
  3: {
    digit: 3,
    name: "Conocimiento",
    noDigits:
      "El interés por las ciencias exactas, la sistematización y el análisis está poco desarrollado. Tiendes a un pensamiento intuitivo y visual. La precisión y la meticulosidad te cuestan esfuerzo.",
    oneDigit:
      "Hay interés por el conocimiento, pero está desarrollado de forma moderada. Te gusta entender el fondo de las cosas, aunque los detalles pueden cansarte pronto. Tienes capacidad de análisis, pero requiere entrenamiento.",
    twoDigits:
      "Buena capacidad analítica y curiosidad. Buscas el conocimiento y sabes sistematizar la información. La ciencia y la investigación te atraen de forma natural.",
    threeDigits:
      "Una marcada inclinación hacia el conocimiento y la precisión. Mente analítica y tendencia al enfoque científico. No aceptas nada sin más: lo compruebas y lo estudias todo.",
    manyDigits:
      "Capacidad cognitiva excepcional. Mente analítica aguda y afán por estudiar los temas en profundidad. La meticulosidad y la exigencia de precisión pueden ser muy elevadas.",
  },
  4: {
    digit: 4,
    name: "Salud",
    noDigits:
      "El cuerpo físico requiere especial atención. La inmunidad puede estar debilitada; es importante llevar un estilo de vida saludable y no ignorar los síntomas de alarma.",
    oneDigit:
      "La salud es media, con ciertos puntos débiles. Manteniendo un buen ritmo y cargas moderadas, es perfectamente posible conservar un buen estado de bienestar.",
    twoDigits:
      "Buena salud física. El cuerpo es bastante resistente y la inmunidad afronta bien las exigencias. La actividad regular ayudará a mantener este nivel durante muchos años.",
    threeDigits:
      "Salud robusta y alto potencial físico. Inmunidad fuerte y buena constitución. El cuerpo se recupera rápido tras las enfermedades y los esfuerzos.",
    manyDigits:
      "Salud de hierro. Un potencial físico poderoso y gran resistencia a las enfermedades. Es importante no abusar de esa reserva y mantener un estilo de vida saludable.",
  },
  5: {
    digit: 5,
    name: "Lógica e intuición",
    noDigits:
      "Ni la lógica ni la intuición son tu herramienta principal. Las decisiones se toman más bien a partir de las emociones o de la opinión ajena. Es importante desarrollar ambos canales de conocimiento.",
    oneDigit:
      "La lógica y la intuición están desarrolladas de forma moderada. Notas los patrones, pero a veces actúas de manera impulsiva. El equilibrio entre el análisis y la percepción aún se está formando.",
    twoDigits:
      "Tienes bien desarrollada la lógica o la intuición, o un equilibrio armónico entre ambas. Sabes tomar decisiones meditadas, combinando el análisis con el instinto.",
    threeDigits:
      "Lógica fuerte e intuición fina. Capacidad de ver conexiones ocultas y anticipar situaciones. Una herramienta analítica poderosa.",
    manyDigits:
      "Capacidad lógico-intuitiva excepcional. Casi siempre conoces la respuesta «correcta», y sabes argumentarla. Un don valioso para el pensamiento estratégico.",
  },
  6: {
    digit: 6,
    name: "Laboriosidad",
    noDigits:
      "La inclinación hacia el trabajo físico y manual es mínima. Prefieres la actividad intelectual u organizativa. El trabajo manual monótono te cansa pronto.",
    oneDigit:
      "Hay laboriosidad, pero es selectiva: trabajas de buena gana en lo que te interesa y con dificultad en lo rutinario. La maestría se desarrolla allí donde hay pasión.",
    twoDigits:
      "Una persona trabajadora y responsable. Sabes llevar las cosas hasta el final y valoras la calidad del resultado. El trabajo manual no te asusta: si quieres, puedes alcanzar la maestría.",
    threeDigits:
      "Gran laboriosidad. Puedes trabajar mucho y con constancia. La maestría se desarrolla de forma natural: disfrutas de un trabajo bien hecho.",
    manyDigits:
      "Laboriosidad y maestría sobresalientes. El trabajo es una parte importante de tu identidad. Riesgo de adicción al trabajo: es importante saber descansar y no sustituir la vida por el trabajo.",
  },
  7: {
    digit: 7,
    name: "Suerte",
    noDigits:
      "La suerte rara vez llega sola: hace falta trabajar mucho para lograr resultados. Es posible que haya lecciones kármicas a través de dificultades que se repiten. La paciencia es tu mayor recurso.",
    oneDigit:
      "La suerte llega, pero no te ha mimado desde el nacimiento: hay que esforzarse. El destino apoya a quienes no se rinden. Las tareas kármicas requieren tomar conciencia.",
    twoDigits:
      "Un nivel medio de suerte. Hay buena fortuna en los momentos clave, y la vida suele acercarte a las personas y oportunidades necesarias. Con la actitud adecuada, la suerte se nota.",
    threeDigits:
      "Suerte palpable. A menudo todo encaja en el momento justo y aparecen las conexiones necesarias. La deuda kármica está, en gran medida, saldada en vidas pasadas.",
    manyDigits:
      "Un nivel de suerte muy alto. La vida parece ayudarte: lo necesario aparece cerca y las oportunidades se abren solas. Es importante administrar este don con gratitud.",
  },
  8: {
    digit: 8,
    name: "Deber y responsabilidad",
    noDigits:
      "El sentido del deber está poco desarrollado. Te cuesta asumir compromisos y cumplirlos sin un control externo. Es importante aprender la responsabilidad como una elección consciente.",
    oneDigit:
      "El sentido de la responsabilidad está presente, pero no domina. Puedes asumir compromisos, aunque a veces evitas las tareas difíciles o poco interesantes.",
    twoDigits:
      "Un sentido del deber desarrollado. Eres una persona fiable en la que se puede confiar. Tomas los compromisos en serio, aunque no se convierten en una obsesión.",
    threeDigits:
      "Un alto sentido de la responsabilidad. Sientes profundamente tus obligaciones hacia tus seres queridos, hacia la sociedad y hacia tus propias promesas. A veces esto pesa: es importante no olvidarte de ti mismo.",
    manyDigits:
      "Un sentido del deber hipertrofiado. Cargas con una responsabilidad enorme, a veces incluso ajena. Es importante aprender a distinguir de qué eres realmente responsable.",
  },
  9: {
    digit: 9,
    name: "Memoria e intelecto",
    noDigits:
      "Las capacidades intelectuales son modestas según la escala de este método. La memoria requiere técnicas específicas. La sabiduría llega a través de la práctica y la experiencia de vida, no de la teoría.",
    oneDigit:
      "La memoria y el intelecto están desarrollados de forma moderada. Eres capaz de aprender y memorizar, pero necesitas más repeticiones. La profundidad de comprensión crece con el tiempo y la experiencia.",
    twoDigits:
      "Buena memoria e intelecto sólido. Asimilas con facilidad la información nueva y sabes extraer conclusiones de la experiencia. La sabiduría se acumula de forma consciente.",
    threeDigits:
      "Excelente memoria y alto intelecto. Capacidad de retener un gran volumen de información y de ver las conexiones profundas. Sabes aprender rápido de la vida.",
    manyDigits:
      "Memoria fenomenal e intelecto sobresaliente. Una verdadera sed de conocimiento, capacidad de análisis profundo y juicios sabios. Maestro y pensador por naturaleza.",
  },
};
